import type { WorkforceAgent } from '../../components/dashboard/AgentStatusWidget.js';
import type { RecentActivityItem } from '../../components/dashboard/RecentActivityWidget.js';
import type { CalendarDeadline } from '../../components/dashboard/CalendarWidget.js';
import type { DashboardKPIStat } from '../../components/dashboard/TodaysStatsWidget.js';
import type { FocusItem } from '../../components/dashboard/FocusTodayWidget.js';
import type { NotificationItem } from '../../components/dashboard/NotificationCenterWidget.js';
import type { PipelineItem } from '../../components/dashboard/ActivePipelinesWidget.js';
import type { BurndownPoint } from '../../components/dashboard/BurndownWidget.js';
import type { DashboardSignals } from './api/dashboard.client.js';
import type { DealListItem } from '../deals/api/deals.client.js';
import type { HotLeadItem } from '../hot-leads/api/hot-leads.client.js';

export interface DashboardViewModel {
  stats: readonly DashboardKPIStat[];
  focusItems: readonly FocusItem[];
  workforce: readonly WorkforceAgent[];
  burndownData: readonly BurndownPoint[];
  pipelines: readonly PipelineItem[];
  activityItems: readonly RecentActivityItem[];
  deadlines: readonly CalendarDeadline[];
  notifications: readonly NotificationItem[];
  onboarding: DashboardOnboardingState;
}

import type { DashboardStats, DashboardActivity, DashboardOnboardingState } from './api/dashboard.client.js';

export interface BuildDashboardViewModelInput {
  deals: readonly DealListItem[];
  hotLeads: readonly HotLeadItem[];
  signals: DashboardSignals;
  stats: DashboardStats;
  activities: readonly DashboardActivity[];
  onboarding: DashboardOnboardingState;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function translateHotLeadReason(reason: HotLeadItem['primaryReason'], t: BuildDashboardViewModelInput['t']): string {
  return t(`crm.hot_leads.reason.${reason}`);
}

export function buildDashboardViewModel({ deals, hotLeads, signals, stats: apiStats, activities, onboarding, t }: BuildDashboardViewModelInput): DashboardViewModel {
  const activeDealsCount = deals.filter((deal) => deal.stage !== 'Won' && deal.stage !== 'Lost').length;
  const wonDealsCount = deals.filter((deal) => deal.stage === 'Won').length;
  const lostDealsCount = deals.filter((deal) => deal.stage === 'Lost').length;
  const closedDealsCount = wonDealsCount + lostDealsCount;
  const successRate = closedDealsCount > 0 ? Math.round((wonDealsCount / closedDealsCount) * 100) : 0;
  const wonRevenue = deals
    .filter((deal) => deal.stage === 'Won')
    .reduce((total, deal) => total + Number(deal.amount), 0);

  const stats: readonly DashboardKPIStat[] = [
    {
      key: 'projects',
      label: 'ОНЛАЙН ЗАПИСИ',
      value: apiStats.dealsTotal > 0 ? apiStats.dealsTotal : deals.length,
      color: 'var(--color-primary, #c47322)',
    },
    {
      key: 'active',
      label: 'СВОБОДНЫЕ ОКНА',
      value: activeDealsCount,
      color: 'var(--color-success, #16a34a)',
    },
    {
      key: 'tasks-done',
      label: 'НОВЫЕ КЛИЕНТЫ',
      value: apiStats.hotLeadsCount > 0 ? apiStats.hotLeadsCount : hotLeads.length,
      color: 'var(--color-info, #2563eb)',
    },
    {
      key: 'success-rate',
      label: 'ВОЗВРАТНОСТЬ',
      value: `${successRate}%`,
      color: 'var(--color-info, #2563eb)',
    },
    {
      key: 'sessions',
      label: 'ОТМЕНЫ',
      value: lostDealsCount,
      color: '#dc2626',
    },
    {
      key: 'total-cost',
      label: 'ВЫРУЧКА (СМЕНА)',
      value: wonRevenue,
      color: 'var(--color-warning, #d97706)',
      suffix: '₽',
    },
  ];

  const focusItems: readonly FocusItem[] = hotLeads.map((lead) => ({
    id: lead.deal.id,
    title: lead.deal.title,
    subtitle: lead.deal.contactName ?? 'Требует внимания',
    status: 'active',
    priority: lead.score >= 80 ? 'P0' : 'P1',
  }));

  const workforce: readonly WorkforceAgent[] = [
    ...signals.scenarioRuns.map((run, index) => ({
      id: run.id,
      name: index === 0 ? 'Напоминание о визите' : index === 1 ? 'Сбор отзыва (WhatsApp)' : 'Реактивация спящих',
      status: (run.status === 'running' ? 'running' : 'offline') as 'running' | 'offline',
      type: 'agent' as const,
      currentProject: index === 0 ? run.scenarioId : run.id,
    })),
    ...signals.pendingApprovals.map((approval) => ({
      id: approval.id,
      name: 'Перенос записи (конфликт)',
      status: (approval.status === 'pending' ? 'running' : 'offline') as 'running' | 'offline',
      type: 'agent' as const,
      currentProject: approval.kind,
    })),
  ];

  const burndownSource = deals
    .map((deal) => deal.lastActivityAt ?? deal.nextActivityDue)
    .filter((value): value is string => value !== null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const burndownData: readonly BurndownPoint[] = burndownSource.map((timestamp, index) => ({
    date: new Date(timestamp).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    ideal: Math.max(burndownSource.length - index - 1, 0),
    remaining: Math.max(activeDealsCount - index, 0),
  }));

  const salonStages = [
    { source: 'New', target: 'Новая запись' },
    { source: 'Contacted', target: 'Требует подтверждения' },
    { source: 'Negotiation', target: 'Переговоры' },
    { source: 'Won', target: 'Услуга оказана' },
  ];

  const pipelines: readonly PipelineItem[] = salonStages
    .map(({ source, target }) => {
      const count = deals.filter((deal) => deal.stage === source).length;
      return {
        id: target,
        name: `${target} · ${count}`,
        status: source === 'Won' ? 'completed' : 'running',
        count,
      };
    });

  const activityItems: readonly RecentActivityItem[] = activities.length > 0
    ? activities.slice(0, 4).map((activity) => ({
      id: activity.id,
      ...(activity.deal?.id ? { dealId: activity.deal.id } : {}),
      title: activity.subject ?? t(`crm.activity.kind.${activity.kind}`),
      message: activity.actor,
      timestamp: activity.occurredAt,
      scopeType: 'task',
    }))
    : deals.slice(0, 4).map((deal) => ({
      id: deal.id,
      dealId: deal.id,
      title: deal.title,
      message: deal.contact ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ') : deal.stage,
      timestamp: deal.lastActivityAt ?? deal.nextActivityDue ?? new Date().toISOString(),
      scopeType: 'task',
    }));

  const deadlines: readonly CalendarDeadline[] = deals
    .filter((deal) => deal.nextActivityDue !== null)
    .slice(0, 3)
    .map((deal) => ({
      id: deal.id,
      title: deal.title,
      date: deal.nextActivityDue ?? new Date().toISOString(),
      project: deal.contact?.firstName ?? deal.title,
      priority: 'P1',
    }));

  const notifications: readonly NotificationItem[] = [
    ...signals.pendingApprovals.map((approval) => ({
      id: approval.id,
      title: t('crm.dashboard.stats.total_cost'),
      body: t('crm.dashboard.workforce.signal.pending_approval'),
      type: 'approval' as const,
      read: false,
      createdAt: approval.createdAt,
    })),
    ...hotLeads.slice(0, 2).map((lead) => ({
      id: `hot-${lead.deal.id}`,
      title: lead.deal.title,
      body: translateHotLeadReason(lead.primaryReason, t),
      type: 'warning' as const,
      read: false,
      createdAt: (deals.find((deal) => deal.id === lead.deal.id)?.nextActivityDue
        ?? deals.find((deal) => deal.id === lead.deal.id)?.lastActivityAt
        ?? new Date().toISOString()) as string,
    })),
  ].slice(0, 4);

  return {
    stats,
    focusItems,
    workforce,
    burndownData,
    pipelines,
    activityItems,
    deadlines,
    notifications,
    onboarding,
  };
}
